import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, BookText, ChevronLeft, ChevronRight, Loader2, Plus, RefreshCw, Search, Settings2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { recordsError } from "../lib/records";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export function ClauseStatus({ clause }) {
  const parts = [];
  if (clause.published_number) parts.push(<span key="p" className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800">Version {clause.published_number} publiée</span>);
  if (clause.has_draft) parts.push(<span key="d" className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">Brouillon en cours</span>);
  if (!clause.active) parts.push(<span key="i" className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-500">Désactivée</span>);
  return <span className="inline-flex flex-wrap gap-1.5">{parts}</span>;
}

// The theme of a NEW clause or of an existing one (an inactive theme is not offered for a new choice, but the clause that has it keeps it).
export function ThemeSelect({ themes, value, onChange, id, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className="h-10" data-testid={testid}><SelectValue placeholder="Choisir…" /></SelectTrigger>
      <SelectContent>{themes.filter((t) => t.active || t.code === value).map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function NewClauseDialog({ open, onClose, themes }) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [theme, setTheme] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setName(""); setTheme(""); setDescription(""); setError(""); setBusy(false); } }, [open]);
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/records/clauses", { name: name.trim(), theme_code: theme, description,
        blocks: [{ kind: "article", text: name.trim() }, { kind: "paragraph", text: "" }] });
      toast.success("Clause créée : son brouillon est ouvert.");
      navigate(`/documents/clauses/${data.id}`);
    } catch (e) {
      setError(recordsError(e, "Création impossible. Réessayez."));
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Nouvelle clause</DialogTitle>
          <DialogDescription>Un morceau de contrat que plusieurs modèles pourront reprendre.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><Label htmlFor="clause-name">Nom</Label>
            <Input id="clause-name" value={name} maxLength={120} onChange={(e) => setName(e.target.value)} className="h-10" placeholder="Ex. Confidentialité — standard" data-testid="clause-name" /></div>
          <div className="space-y-1.5"><Label htmlFor="clause-theme">Thème</Label>
            <ThemeSelect themes={themes} value={theme} onChange={setTheme} id="clause-theme" testid="clause-theme" /></div>
          <div className="space-y-1.5"><Label htmlFor="clause-description">Quand l'utiliser (facultatif)</Label>
            <Textarea id="clause-description" value={description} rows={2} maxLength={4000} onChange={(e) => setDescription(e.target.value)} data-testid="clause-description" /></div>
        </div>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Fermer</Button>
          <Button onClick={submit} disabled={busy || name.trim().length < 2 || !theme} data-testid="clause-create">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}Créer la clause
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Themes: label, order and activity — renaming never touches a clause (D-108).
function ThemesDialog({ open, onClose, themes, onChanged }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  useEffect(() => { if (open) { setRows(themes.map((t) => ({ ...t }))); setError(""); } }, [open, themes]);
  const save = async (row) => {
    setBusy(row.code);
    setError("");
    try {
      await api.patch(`/records/clause-themes/${row.code}`, { label: row.label.trim(), order: Number(row.order), active: row.active });
      toast.success("Thème enregistré.");
      await onChanged();
    } catch (e) {
      setError(recordsError(e, "Enregistrement impossible."));
    } finally {
      setBusy("");
    }
  };
  const change = (code, patch) => setRows((list) => list.map((r) => (r.code === code ? { ...r, ...patch } : r)));
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Thèmes des clauses</DialogTitle>
          <DialogDescription>Renommez, réordonnez ou désactivez un thème ; ses clauses le gardent.</DialogDescription>
        </DialogHeader>
        <ul className="space-y-2" data-testid="themes-list">
          {rows.map((r) => {
            const saved = themes.find((t) => t.code === r.code);
            const dirty = saved && (saved.label !== r.label.trim() || String(saved.order) !== String(r.order) || saved.active !== r.active);
            return (
              <li key={r.code} className="flex flex-wrap items-center gap-2" data-testid={`theme-${r.code}`}>
                <Input value={r.label} maxLength={80} aria-label={`Libellé du thème ${r.code}`} onChange={(e) => change(r.code, { label: e.target.value })} className="h-9 min-w-0 flex-1" data-testid={`theme-${r.code}-label`} />
                <Input type="number" min={0} max={1000} value={r.order} aria-label={`Ordre du thème ${r.label}`} onChange={(e) => change(r.code, { order: e.target.value })} className="h-9 w-20" />
                <Switch checked={r.active} onCheckedChange={(v) => change(r.code, { active: v })} aria-label={`Thème ${r.label} actif`} data-testid={`theme-${r.code}-active`} />
                <Button size="sm" variant="outline" disabled={!dirty || r.label.trim().length < 2 || busy === r.code} onClick={() => save(r)} data-testid={`theme-${r.code}-save`}>Enregistrer</Button>
              </li>
            );
          })}
        </ul>
        {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function RecordClauses() {
  const [data, setData] = useState(null);
  const [themes, setThemes] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [theme, setTheme] = useState("all");
  const [showInactive, setShowInactive] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingThemes, setEditingThemes] = useState(false);

  const loadThemes = useCallback(async () => {
    try {
      const { data: body } = await api.get("/records/clause-themes");
      setThemes(body.items);
    } catch (e) {
      setThemes([]);
    }
  }, []);
  const load = useCallback(async () => {
    setError("");
    try {
      const { data: body } = await api.get("/records/clauses", { params: { search: search || undefined, theme: theme === "all" ? undefined : theme, include_inactive: showInactive } });
      setData(body);
    } catch (e) {
      setError(recordsError(e, "Impossible de charger la bibliothèque."));
    }
  }, [search, theme, showInactive]);
  useEffect(() => { loadThemes(); }, [loadThemes]);
  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);
  const canManage = !!data?.can_manage;
  const filtered = search !== "" || theme !== "all";

  return (
    <div className="space-y-4" data-testid="clauses-page">
      <Link to="/documents" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ChevronLeft size={14} />Documents et contrats</Link>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Clauses</h1>
          <p className="text-sm text-slate-500">Des morceaux de contrat écrits une fois et repris par plusieurs modèles, avec leur version.</p>
        </div>
        {canManage && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setEditingThemes(true)} data-testid="clauses-themes"><Settings2 size={16} className="mr-1.5" />Thèmes</Button>
            <Button onClick={() => setCreating(true)} data-testid="clauses-new"><Plus size={16} className="mr-1.5" />Nouvelle clause</Button>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="relative flex-1 sm:max-w-sm">
          <Search size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une clause…" aria-label="Rechercher une clause" className="h-10 pl-9" data-testid="clauses-search" />
        </div>
        <Select value={theme} onValueChange={setTheme}>
          <SelectTrigger className="h-10 sm:w-56" aria-label="Thème" data-testid="clauses-theme-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les thèmes</SelectItem>
            {themes.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {canManage && (
          <label className="flex items-center gap-2 text-sm text-slate-600 sm:ml-auto">
            <Switch checked={showInactive} onCheckedChange={setShowInactive} data-testid="clauses-inactive" />Afficher les clauses désactivées
          </label>
        )}
      </div>

      {error && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {!data && !error && <div className="space-y-2"><Skeleton className="h-14 w-full" /><Skeleton className="h-14 w-full" /></div>}
      {data && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="clauses-empty">
          <BookText className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">{filtered ? "Aucune clause ne correspond" : canManage ? "Créez votre première clause" : "Aucune clause publiée"}</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            {filtered ? "Essayez un autre mot ou un autre thème."
              : canManage ? "Écrivez une fois la confidentialité, la résiliation ou le paiement ; chaque modèle la reprend en gardant la version qu'il a choisie."
                : "Un responsable des modèles doit d'abord publier une clause."}
          </p>
        </Card>
      )}
      {data && data.items.length > 0 && (
        <Card className="divide-y divide-slate-100" data-testid="clauses-list">
          {data.items.map((c) => (
            <Link key={c.id} to={`/documents/clauses/${c.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50" data-testid={`clause-${c.id}`}>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-slate-900 break-words">{c.name}</span>
                  <span className="rounded-full bg-jamora-50 px-2 py-0.5 text-xs font-medium text-jamora">{c.theme_label}</span>
                  {canManage && <ClauseStatus clause={c} />}
                </div>
                {c.description && <p className="text-xs text-slate-500 truncate">{c.description}</p>}
                {canManage && <p className="text-xs text-slate-400" data-testid={`clause-${c.id}-usage`}>{c.used_by_templates === 0 ? "Utilisée par aucun modèle" : `Utilisée par ${c.used_by_templates} modèle${c.used_by_templates > 1 ? "s" : ""}`}</p>}
              </div>
              {!canManage && c.published_number && <span className="shrink-0 text-xs text-slate-500">Version {c.published_number}</span>}
              <ChevronRight size={16} className="shrink-0 text-slate-300" />
            </Link>
          ))}
        </Card>
      )}
      <NewClauseDialog open={creating} onClose={() => setCreating(false)} themes={themes} />
      <ThemesDialog open={editingThemes} onClose={() => setEditingThemes(false)} themes={themes} onChanged={async () => { await loadThemes(); await load(); }} />
    </div>
  );
}
